import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Checkbox } from 'lib/ui';
import { IClinicDayColumn } from 'model/ops';
import {
  groupByDiscipline,
  groupStateOf,
  matchesSearch,
  orderColumns,
  selectionLabel,
  toggleClinician,
  toggleGroup
} from 'utils/mappers/response/clinicDayGrid';
import { formatProviderName } from 'utils/format';
import { PickerWrapper } from './style';

/**
 * AXON-3585: choosing which clinicians the day grid loads.
 *
 * A clinic can staff a dozen people, and the questions this view answers are
 * usually about two of them — where to move one child, or where two clinicians
 * could see one. So the grid opens with everyone and this narrows it.
 */


interface Props {
  columns: IClinicDayColumn[];
  selected: number[];
  onChange: (next: number[]) => void;
  onReset: () => void;
  /**
   * Whether anything has been narrowed OR rearranged. The page owns both, and
   * Reset undoes both — the picker cannot see the column order from here.
   */
  canReset: boolean;
}

export const ClinicianPicker: React.FC<Props> = ({
  columns,
  selected,
  onChange,
  onReset,
  canReset
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<string[]>([]);
  const pickerRef = useRef<HTMLDivElement>(null);

  // The panel floats over the grid, so while it is open the slots and column
  // headers beneath it cannot be clicked. Without this it stays open until the
  // button is pressed again, and the page reads as though it has stopped
  // responding — clicking "away" is what everyone tries first.
  useEffect(() => {
    if (!open) return undefined;

    const closeOnOutside = (event: MouseEvent) => {
      if (!pickerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', closeOnOutside);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutside);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [open]);

  const groups = useMemo(() => groupByDiscipline(columns), [columns]);

  // Searching narrows the list and opens whichever groups still have members,
  // so a name typed in full is visible without a second click.
  const visibleGroups = useMemo(
    () =>
      groups
        .map(group => ({
          ...group,
          members: group.members.filter(member => matchesSearch(member, search))
        }))
        .filter(group => group.members.length > 0),
    [groups, search]
  );

  const searching = !!search.trim();
  const isExpanded = (discipline: string) =>
    searching || expanded.includes(discipline);

  const toggleExpanded = (discipline: string) =>
    setExpanded(current =>
      current.includes(discipline)
        ? current.filter(item => item !== discipline)
        : [...current, discipline]
    );

  // A readout of who is loaded. Arranging the columns happens on the columns
  // themselves — that is where people reach for it, and mixing the two made the
  // strip look like a filter that was secretly also a layout control.
  const selectedColumns = useMemo(
    () =>
      orderColumns(
        columns.filter(column => selected.includes(column.providerId))
      ),
    [columns, selected]
  );

  return (
    <PickerWrapper>
      {/* Pinned left, so it keeps one place however many chips follow it. */}
      <div className="picker" ref={pickerRef}>
        <button
          type="button"
          className="picker-button"
          aria-expanded={open}
          aria-haspopup="true"
          onClick={() => setOpen(current => !current)}
        >
          {selectionLabel(selected.length, columns.length)}
          <span className="caret" aria-hidden="true">
            ▾
          </span>
        </button>

        {open && (
          <div className="picker-panel">
            <input
              type="search"
              value={search}
              placeholder="Search name or discipline"
              aria-label="Search clinicians"
              autoComplete="off"
              onChange={event => setSearch(event.target.value)}
            />

            <div className="picker-list">
              {visibleGroups.length === 0 && (
                <p className="picker-empty">No clinician matches “{search}”.</p>
              )}

              {visibleGroups.map(group => {
                const memberIds = group.members.map(
                  member => member.providerId
                );
                const state = groupStateOf(memberIds, selected);
                return (
                  <React.Fragment key={group.discipline}>
                    <div className="picker-row group">
                      <Checkbox
                        checked={state === 'all'}
                        indeterminate={state === 'some'}
                        onChange={() =>
                          onChange(toggleGroup(selected, memberIds))
                        }
                      >
                        All {group.discipline}{' '}
                        <span className="count">({group.members.length})</span>
                      </Checkbox>
                      <button
                        type="button"
                        className="expander"
                        aria-expanded={isExpanded(group.discipline)}
                        aria-label={`Show ${group.discipline} clinicians`}
                        onClick={() => toggleExpanded(group.discipline)}
                      >
                        {isExpanded(group.discipline) ? '▾' : '▸'}
                      </button>
                    </div>

                    {isExpanded(group.discipline) &&
                      group.members.map(member => (
                        <div
                          className="picker-row child"
                          key={member.providerId}
                        >
                          <Checkbox
                            checked={selected.includes(member.providerId)}
                            onChange={() =>
                              onChange(
                                toggleClinician(selected, member.providerId)
                              )
                            }
                          >
                            {formatProviderName(member)}{' '}
                            <span className="count">
                              {member.specialtyAbbreviation}
                            </span>
                          </Checkbox>
                        </div>
                      ))}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Flows after the control rather than displacing it. */}
      <div className="chips">
        {selectedColumns.map(column => (
          <span className="chip" key={column.providerId}>
            {formatProviderName(column)}
            {/* Always offered. Hiding this until something was already narrowed
                made the strip a dead end: removing a clinician is how you
                narrow in the first place, so there was no way in. */}
            <button
              type="button"
              aria-label={`Remove ${formatProviderName(column)}`}
              onClick={() =>
                onChange(toggleClinician(selected, column.providerId))
              }
            >
              ×
            </button>
          </span>
        ))}
      </div>

      {canReset && (
        <button type="button" className="reset" onClick={onReset}>
          Reset
        </button>
      )}
    </PickerWrapper>
  );
};
